// Country-specific NextSteps loader.
//
// JSON lives in `web/public/data/next-steps-by-country.json`; structure is
// `Record<CountryCode, CountryNextStepsData>` with bilingual labels/details
// per checklist item. Single fetch, cached forever (`staleTime: Infinity`).
// Italy is NOT in the JSON — it stays hardcoded in NextStepsPage for now to
// preserve translations baked into i18n keys (`nextSteps.item.*` etc.).
import { useQuery } from '@tanstack/react-query';

export interface ChecklistItem {
  id: string;
  label_en: string;
  label_ru: string;
  detail_en: string;
  detail_ru: string;
}

export interface ResourceLink {
  label: string;
  url: string;
  note?: string;
}

export interface CountryNextStepsData {
  name_en: string;
  name_ru: string;
  phases: {
    strategy: ChecklistItem[];
    prep: ChecklistItem[];
    logistics: ChecklistItem[];
    arrival: ChecklistItem[];
    settle: ChecklistItem[];
  };
  resources: {
    strategy: ResourceLink[];
    prep: ResourceLink[];
    logistics: ResourceLink[];
    arrival: ResourceLink[];
    settle: ResourceLink[];
  };
}

export type CountryNextStepsFile = Record<string, CountryNextStepsData> & {
  _meta?: {
    version?: string;
    generated?: string;
    language?: string;
    note?: string;
  };
};

export const PHASE_ORDER = ['strategy', 'prep', 'logistics', 'arrival', 'settle'] as const;
export type PhaseId = (typeof PHASE_ORDER)[number];

export function useCountryNextSteps(countryCode: string | null) {
  return useQuery({
    queryKey: ['next-steps-by-country'],
    queryFn: async (): Promise<CountryNextStepsFile> => {
      const res = await fetch('/data/next-steps-by-country.json');
      if (!res.ok) throw new Error(`next-steps-by-country.json: ${res.status}`);
      return res.json();
    },
    staleTime: Infinity,
    gcTime: Infinity,
    select: (file): CountryNextStepsData | null => {
      if (!countryCode) return null;
      const upper = countryCode.toUpperCase();
      return (file[upper] as CountryNextStepsData | undefined) ?? null;
    },
    // The full file is small (~100 KB) but we only need it if we're rendering
    // a non-IT country. Italy renders from the hardcoded constants.
    enabled: !!countryCode && countryCode.toUpperCase() !== 'IT',
  });
}
